import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Button, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { useAlertModal } from '../../AlertModalContext'; // Importar o hook

const LancamentoDespesasTab = ({
  currentEntidade, // Necessário para chamadas API
  formatCurrency, // Função de formatação
  // Props para o modal de exclusão (gerenciado no pai)
  onDeleteClick, // Função para abrir o modal de exclusão no pai
  isActive, // Propriedade para indicar se a aba está ativa
}) => {
  const [novaDespesa, setNovaDespesa] = useState({
    categoria: 'outras_despesas_administrativas',
    descricao: '',
    valor: '',
    data_despesa: new Date().toISOString().split('T')[0],
  });
  const [isSavingDespesa, setIsSavingDespesa] = useState(false);
  const [despesasLancadas, setDespesasLancadas] = useState([]);
  const [isLoadingDespesasLancadas, setIsLoadingDespesasLancadas] = useState(false);
  const { showAlertModal } = useAlertModal(); // Usar o hook

  const handleNovaDespesaChange = (e) => {
    const { name, value } = e.target;
    setNovaDespesa(prev => ({ ...prev, [name]: value }));
  };

  const fetchDespesasLancadas = async () => {
    if (!currentEntidade) return;
    setIsLoadingDespesasLancadas(true);
    try {
      const response = await axios.get('/api/despesas-operacionais', { params: { entidade_id: currentEntidade.id } });
      setDespesasLancadas(response.data.despesas || []);
    } catch (error) {
      console.error("Erro ao buscar despesas lançadas:", error);
      setDespesasLancadas([]);
      showAlertModal('Erro', 'Não foi possível carregar as despesas recentes.', 'danger');
    } finally {
      setIsLoadingDespesasLancadas(false);
    }
  };

  const handleSalvarNovaDespesa = async (e) => {
    e.preventDefault();
    if (!currentEntidade) { showAlertModal('Erro', 'Entidade não selecionada.', 'danger'); return; }
    setIsSavingDespesa(true);
    try {
      const payload = { ...novaDespesa, entidade_id: currentEntidade.id };
      await axios.post('/api/despesas-operacionais', payload);
      showAlertModal('Sucesso', 'Despesa registrada com sucesso!', 'success');
      setNovaDespesa({ categoria: 'outras_despesas_administrativas', descricao: '', valor: '', data_despesa: new Date().toISOString().split('T')[0] });
      fetchDespesasLancadas();
    } catch (error) { showAlertModal('Erro', `Falha ao registrar despesa: ${error.response?.data?.message || error.message}`, 'danger'); }
    finally { setIsSavingDespesa(false); }
  };

  // Efeito para carregar despesas quando a entidade muda ou a aba é ativada (assumindo que o pai gerencia a ativação)
  useEffect(() => {
    if (isActive && currentEntidade) { // Só busca se a aba estiver ativa E a entidade carregada
      fetchDespesasLancadas();
    }
  }, [isActive, currentEntidade]); // Adiciona isActive às dependências

  return (
    <>
      <Card className="mb-4 bg-light border-0 shadow-sm">
        <Card.Body>
          <Card.Title as="h6">Para que serve esta funcionalidade?</Card.Title>
          <Card.Text className="small text-muted">
            Aqui você pode registrar os gastos mensais da sua entidade, como contas de água, luz, aluguel, marketing e outros custos operacionais.
            Manter esses registros atualizados é fundamental para a saúde financeira do seu negócio e para gerar relatórios precisos como o DRE (Demonstrativo de Resultado do Exercício).
          </Card.Text>
        </Card.Body>
      </Card>
      <Card className="mb-4">
        <Card.Header><h4>Registrar Nova Despesa Operacional</h4></Card.Header>
        <Card.Body>
          <Form onSubmit={handleSalvarNovaDespesa}>
            <Row className="mb-3">
              <Col md={4}><Form.Group controlId="despesaCategoriaTab"><Form.Label>Categoria*</Form.Label><Form.Select name="categoria" value={novaDespesa.categoria} onChange={handleNovaDespesaChange} required><option value="aluguel_condominio">Aluguel e Condomínio</option><option value="agua_luz_telefone_internet">Água, Luz, Telefone, Internet</option><option value="marketing_publicidade">Marketing e Publicidade</option><option value="material_escritorio_limpeza">Material Escritório/Limpeza</option><option value="outras_despesas_administrativas">Outras Despesas Administrativas</option></Form.Select></Form.Group></Col>
              <Col md={4}><Form.Group controlId="despesaDataTab"><Form.Label>Data da Despesa*</Form.Label><Form.Control type="date" name="data_despesa" value={novaDespesa.data_despesa} onChange={handleNovaDespesaChange} required /></Form.Group></Col>
              <Col md={4}><Form.Group controlId="despesaValorTab"><Form.Label>Valor (R$)*</Form.Label><Form.Control type="number" step="0.01" name="valor" placeholder="0.00" value={novaDespesa.valor} onChange={handleNovaDespesaChange} required /></Form.Group></Col>
            </Row>
            <Row className="mb-3"><Col md={12}><Form.Group controlId="despesaDescricaoTab"><Form.Label>Descrição (Opcional)</Form.Label><Form.Control as="textarea" rows={2} name="descricao" placeholder="Detalhes da despesa" value={novaDespesa.descricao} onChange={handleNovaDespesaChange} /></Form.Group></Col></Row>
            <Button variant="primary" type="submit" disabled={isSavingDespesa}>{isSavingDespesa ? <Spinner as="span" animation="border" size="sm" /> : 'Salvar Despesa'}</Button>
          </Form>
        </Card.Body>
      </Card>
      <Card className="mt-4">
        <Card.Header><Row className="align-items-center"><Col><h4>Despesas Lançadas Recentemente</h4></Col><Col xs="auto"><Button variant="outline-primary" size="sm" onClick={fetchDespesasLancadas} disabled={isLoadingDespesasLancadas}>{isLoadingDespesasLancadas ? <Spinner as="span" animation="border" size="sm" /> : 'Atualizar Lista'}</Button></Col></Row></Card.Header>
        <Card.Body>
          {isLoadingDespesasLancadas ? <Spinner animation="border" /> : (
            despesasLancadas.length > 0 ? (
              <Table striped bordered hover responsive size="sm">
                <thead>
                  <tr>
                    <th className="d-none d-sm-table-cell">Data</th> {/* Oculta em xs, mostra a partir de sm */}
                    <th>Categoria</th>
                    <th>Descrição</th>
                    <th className="text-end">Valor (R$)</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {despesasLancadas.map(despesa => (<tr key={despesa.id}><td className="d-none d-sm-table-cell">{new Date(despesa.data_despesa).toLocaleDateString('pt-BR')}</td><td>{despesa.categoria.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</td><td>{despesa.descricao}</td><td className="text-end">{formatCurrency(despesa.valor)}</td><td><Button variant="outline-danger" size="sm" onClick={() => onDeleteClick(despesa)}>
                    <span className="d-none d-md-inline">Excluir</span>
                    <span className="d-inline d-md-none">X</span>
                  </Button></td></tr>))}
                </tbody>
              </Table>
            ) : <p>Nenhuma despesa lançada recentemente.</p>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default LancamentoDespesasTab;